/// <amd-module name="Release/Landing/Artifact" />
import { IVssPageContext } from "VSS/Platform/Context";
import { startsWith, equals, format } from "VSS/Core/Util/String";

import { Artifact, ArtifactSourceReference } from "Release/Client/Contracts/Release";
import { createUrlForRoute } from "Release/Helpers/Link";

const BranchId = "branch";
const Version = "version";

export namespace ArtifactTypes {
    export const BuildArtifactType = "Build";
    export const GitArtifactType = "Git";
    export const GitHubArtifactType = "GitHub";
    export const JenkinsArtifactType = "Jenkins";
    export const TfvcArtifactType = "TFVC";
    export const NugetArtifactType = "Nuget";
    export const TfsOnPremArtifactType = "Team Build (external)";
    export const ExternalGitArtifactType = "ExternalGit";
    export const ExternalTfsBuildArtifactType = "ExternalTFSBuild";
    export const SvnArtifactType = "Svn";
    export const ExternalTfsXamlBuildArtifactType = "ExternalTfsXamlBuild";
    export const PackageManagementArtifactType = "PackageManagement";
    export const DockerHubArtifactType = "DockerHub";
    export const AzureContainerRepositoryArtifactType = "AzureContainerRepository";
}

namespace ArtifactDefinitionConstants {
    export const ProjectId = "project";
    export const Version = "version";
    export const ArtifactSourceVersionUrl = "artifactSourceVersionUrl";
    export const IsMultiDefinitionType = "IsMultiDefinitionType";
    export const Definition = "definition";
}

export function getPrimaryArtifact(artifacts: Artifact[]): Artifact | undefined {
    if (!artifacts || artifacts.length <= 0) {
        return;
    }

    return artifacts.find((artifact: Artifact): boolean => !!artifact && artifact.isPrimary);
}

export function getPrimaryArtifactBranchName(artifacts: Artifact[]): string {
    let primaryArtifact: Artifact | undefined = getPrimaryArtifact(artifacts);
    return getArtifactBranchName(primaryArtifact);
}

export function getArtifactBranchName(artifact: Artifact | undefined): string {
    if (artifact) {
        const sourceBranch: ArtifactSourceReference = artifact.definitionReference[BranchId];
        return (sourceBranch && sourceBranch.name) ? sourceBranch.name : "";
    }

    return "";
}

export function getArtifactBuildInfo(artifact: Artifact | undefined): string {
    if (artifact) {
        const artifactVersion: ArtifactSourceReference = artifact.definitionReference[Version];
        var artifactDefinition: ArtifactSourceReference = artifact.definitionReference[ArtifactDefinitionConstants.Definition];
        if (artifactVersion && artifactVersion.name) {
            if (artifactDefinition && artifactDefinition.name && isMultiDefinitionType(artifact)) {
                return format("{0} ({1})", artifactVersion.name, artifactDefinition.name)
            }

            return artifactVersion.name;
        }
    }

    return "";
}

function isMultiDefinitionType(artifact: Artifact | undefined): boolean {
    if (artifact
        && artifact.definitionReference
        && artifact.definitionReference.hasOwnProperty(ArtifactDefinitionConstants.IsMultiDefinitionType)
        && equals(artifact.definitionReference[ArtifactDefinitionConstants.IsMultiDefinitionType].id, "true", true)) {
        return true;
    }

    return false;
}

export function getArtifactVersionUrl(pageContext: IVssPageContext, artifact: Artifact | undefined): string {
    if (artifact) {
        const version = artifact.definitionReference[ArtifactDefinitionConstants.Version];
        if (!version || !version.id) {
            return "";
        }

        const project = artifact.definitionReference[ArtifactDefinitionConstants.ProjectId];
        const projectName = project && (project.name || project.id);

        if (artifact.type === ArtifactTypes.BuildArtifactType) {
            const routeValues: { [key: string]: string } = { "buildId": version.id };
            if (projectName) {
                routeValues["project"] = projectName;
            }

            let artifactUrl = "";
            try {
                artifactUrl = createUrlForRoute(pageContext, "ms.vss-build-web.ci-results-hub-route", routeValues);
            }
            catch {
                // Do not show links in this case, only text
                // TODO - trace this
            }

            return artifactUrl;
        }

        if (artifact.type === ArtifactTypes.TfvcArtifactType) {
            const routeValues: { [key: string]: string } = { "parameters": version.id };
            if (projectName) {
                routeValues["project"] = projectName;
            }

            let artifactUrl = "";
            try {
                return createUrlForRoute(pageContext, "ms.vss-code-web.tfvc-changeset-details-route", routeValues);
            }
            catch {
                // Do not show links in this case, only text
                // TODO - trace this
            }

            return artifactUrl;
        }

        if (isArtifactVersionLinkAvailable(artifact.type)) {
            const artifactSourceVersionUrl: ArtifactSourceReference = artifact.definitionReference[ArtifactDefinitionConstants.ArtifactSourceVersionUrl];
            return (artifactSourceVersionUrl && artifactSourceVersionUrl.id) ? artifactSourceVersionUrl.id : "";
        }
    }

    return "";
}

export function getArtifactIcon(artifactType: string): string {
    return iconMapping[artifactType] || "Build";
}

export function getBranchDisplayValue(branch: string): string {
    let trimmedBranch: string = branch;
    const branchPrefix = "refs/heads/";

    if (!!branch && startsWith(branch, branchPrefix)) {
        trimmedBranch = branch.substr(branchPrefix.length);
    }

    return trimmedBranch;
}


function isArtifactVersionLinkAvailable(artifactTypeName: string): boolean {
    switch (artifactTypeName) {
        case ArtifactTypes.BuildArtifactType:
        case ArtifactTypes.GitArtifactType:
        case ArtifactTypes.TfvcArtifactType:
            return true;
    }

    return false;
}

const iconMapping: { [key: string]: string } = {
    [ArtifactTypes.BuildArtifactType]: "Build",
    [ArtifactTypes.ExternalTfsBuildArtifactType]: "ExternalBuild",
    [ArtifactTypes.GitArtifactType]: "GitFork",
    [ArtifactTypes.GitHubArtifactType]: "GitHubLogo",
    [ArtifactTypes.JenkinsArtifactType]: "JenkinsLogo",
    [ArtifactTypes.NugetArtifactType]: "NugetLogo",
    [ArtifactTypes.PackageManagementArtifactType]: "Giftbox",
    [ArtifactTypes.TfsOnPremArtifactType]: "VSTSLogo",
    [ArtifactTypes.TfvcArtifactType]: "TFVCLogo"
};
